import React, { Fragment } from "react";
import { useFormikContext } from "formik";
import { extendSelectOptions, getSelectNotice } from "utils/form";
import startCase from "lodash/startCase";
import classnames from "classnames";
import TextareaController from "components/TextareaController";
import SelectField from "components/SelectField";
import get from "lodash/get";
import { schemaPropertiesToValues } from "components/FormerEditor/common";
import {
  isDefaultCategory,
  isHiddenCategory,
  isTemplateComponent,
  isPickerComponent,
  isSelectComponent
} from "utils/schema";
import { getMeetsDependencies } from "utils/builder";
import {
  SCHEMA_TYPES,
  PAGE_ACTION_UIDS
} from "components/FormerEditor/common/constants";
import FieldGroup from "./";
import { useAdminContext, useUserContext } from "utils/state";
import { captureException } from "utils/error";
import CheckoutShortLinkPicker from "components/CheckoutShortLinkPicker";
import { useCtxReferences } from "utils/selectors";
import StripeConnectActions from "components/StripeConnectActions";

const NOTICE_COMPONENT_MAP = {
  [PAGE_ACTION_UIDS.STRIPE_CHECKOUT]: ({ authAction, header, subheader }) => {
    let trigger = authAction ? <StripeConnectActions /> : null;

    return (
      <div className="w-100 flex flex-column justify-between pv3 ba br2 hairline-1 pa3">
        {header && <div className="fw5">{header}</div>}
        {subheader && (
          <div className={classnames("f7 lh-copy", { pt2: header })}>
            {subheader}
          </div>
        )}
        {trigger && (
          <div className="flex justify-start w-100 pt3">{trigger}</div>
        )}
      </div>
    );
  }
};

const getTemplateField = () => {
  captureException(new Error("Template fields are not yet supported"));

  return null;
};

const getSelectField = ({ label, fieldName, user, admin, schema }) => {
  const selectProps = {
    label,
    name: fieldName,
    options: extendSelectOptions(schema.options, { admin })
  };

  const notice = getSelectNotice(schema.options, { user, admin });
  const NoticeComponent = notice && NOTICE_COMPONENT_MAP[notice.uid];

  return (
    <Fragment>
      <SelectField {...selectProps} />
      {NoticeComponent && (
        <div className="pt3">
          <NoticeComponent {...notice} />
        </div>
      )}
    </Fragment>
  );
};

/**
 * Access the UI config off of the component node
 * ui: {uuid: }
 * background-color:
 * font-size:
 * font-weight:
 */
const FieldGroupComponent = ({
  rootName,
  name,
  schema,
  variableContext,
  dependencies
}) => {
  const { values } = useFormikContext();
  const { application } = useCtxReferences();
  const admin = useAdminContext({
    applicationUUID: application.uuid
  });
  const user = useUserContext();

  const nodes = Object.keys(schema).reduce((memo, fieldKey, fieldKeyIx) => {
    const fieldName = `${name}.${fieldKey}`;
    const ctxSchema = schema[fieldKey];
    const label = ctxSchema.title || startCase(fieldKey);

    let content = null;

    /**
     * If schema type is an array
     * - then recursively generate and render components per schema definition
     */
    if (ctxSchema.type === SCHEMA_TYPES.ARRAY) {
      const groupSchema = ctxSchema.items.properties;
      const subDependencies = ctxSchema.items.dependencies;
      const template = schemaPropertiesToValues(groupSchema);

      content = (
        <FieldGroup
          label={ctxSchema.title}
          name={fieldName}
          collection={get(values, fieldName)}
          schema={groupSchema}
          template={template}
          dependencies={subDependencies}
        />
      );
    } else {
      const meetsDependencies = getMeetsDependencies({
        values,
        dependencies,
        name,
        fieldKey
      });

      /**
       * If schema type is default (i.e. editable by the user)
       * - then render components per schema component definition
       */
      const canRenderHiddenPicker =
        isHiddenCategory(ctxSchema) &&
        isPickerComponent(ctxSchema) &&
        meetsDependencies;
      const isValidDefault = isDefaultCategory(ctxSchema) && meetsDependencies;
      if (canRenderHiddenPicker) {
        content = <CheckoutShortLinkPicker name={fieldName} editMode />;
      } else if (isValidDefault) {
        const isTemplate = isTemplateComponent(ctxSchema);
        const isSelect = isSelectComponent(ctxSchema);

        if (isTemplate) {
          content = getTemplateField();
        } else if (isSelect) {
          content = getSelectField({
            label,
            fieldName,
            user,
            admin,
            schema: ctxSchema
          });
        } else {
          content = (
            <TextareaController
              name={fieldKey}
              label={label}
              key={`${name}.${fieldKey}`}
              rootPath={name}
              fieldKey={fieldKey}
              minRows={1}
            />
          );
        }
      }
    }

    if (content) {
      memo.push(
        <div
          key={fieldKeyIx}
          className={classnames("w-100", {
            pt3: fieldKeyIx
          })}
        >
          {content}
        </div>
      );
    }
    return memo;
  }, []);

  return <div className="w-100 flex flex-column">{nodes}</div>;
};

export default FieldGroupComponent;
